import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Compass, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHAMPS = [
  { key: 'objectif_professionnel', label: 'Objectif professionnel', hint: 'Projet, poste visé, évolution souhaitée' },
  { key: 'attentes_formation', label: 'Attentes personnelles vis-à-vis de la formation', hint: 'Ce que le candidat attend concrètement de la formation' },
  { key: 'experience_professionnelle', label: 'Expérience professionnelle', hint: 'Parcours, métiers exercés, compétences déjà acquises' },
  { key: 'ressenti_formateur', label: 'Ressenti du candidat (par le formateur)', hint: 'Motivation, aisance, freins perçus par le formateur' },
];
const EMPTY = Object.fromEntries(CHAMPS.map(c => [c.key, '']));

// Positionnement du candidat sur l'action de formation
export const PositionnementPanel = ({ dossierId, candidatId, canEdit }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setData(null);
    axios.get(`${API_URL}/dossiers/${dossierId}/candidats/${candidatId}/positionnement`).then(r => { setData(r.data); setForm({ ...EMPTY, ...Object.fromEntries(CHAMPS.map(c => [c.key, r.data[c.key] || ''])) }); }).catch(() => setData({}));
  }, [dossierId, candidatId]);
  const dirty = data && CHAMPS.some(c => (data[c.key] || '') !== form[c.key]);
  const save = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API_URL}/dossiers/${dossierId}/candidats/${candidatId}/positionnement`, form); setData(r.data); toast.success('Positionnement enregistré'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };
  if (!data) return <Card className="border-0 shadow-sm"><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></CardContent></Card>;
  return (
    <Card className="border-0 shadow-sm" data-testid="positionnement-panel">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Compass className="w-4 h-4 text-indigo-500" /> Positionnement du candidat</h2>
            {data.updated_at && <p className="text-xs text-slate-400 mt-0.5">Mis à jour par {data.auteur_nom} le {format(parseISO(data.updated_at), 'dd/MM/yyyy HH:mm')}</p>}
          </div>
          {canEdit && <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={!dirty || saving} onClick={save} data-testid="positionnement-save-btn"><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</Button>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CHAMPS.map(ch => (
            <div key={ch.key} className="space-y-1" data-testid={`positionnement-field-${ch.key}`}>
              <p className="text-xs font-semibold text-slate-600">{ch.label}</p>
              <Textarea rows={2} value={form[ch.key]} disabled={!canEdit} onChange={(e) => setForm({ ...form, [ch.key]: e.target.value })} className="text-sm" placeholder={canEdit ? ch.hint : '—'} data-testid={`positionnement-textarea-${ch.key}`} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
